/**
 * AiImagePicker — Sprint 14 · reusable image-generation picker.
 *
 * Used from AdminAiImageStudio, AdminLeads reply drawer and AdminSeo hero
 * editor. Supports text-to-image (both models) plus image-to-image / reference
 * generation (Nano Banana only). All calls stream through /api/ai-studio/*
 * behind require_admin.
 *
 * Props:
 *   - onSelect(image) : called when admin approves a generated image
 *   - onClose()       : dismiss the picker
 *   - contextLabel    : e.g. "AdminLeads reply" — surfaces in the header
 *   - allowReference  : boolean · defaults true · hides the reference tab if false
 *   - defaultPrompt   : optional seed prompt
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const MODEL_OPTIONS = [
  { id: "nano-banana", label: "Gemini Nano Banana", desc: "Photorealistic · supports reference image" },
  { id: "gpt-image-1", label: "OpenAI GPT Image 1", desc: "Editorial / art-directed · text-only" },
];
const ASPECTS = ["1:1", "16:9", "4:3", "3:2", "9:16", "2:3", "4:5"];
const QUALITIES = ["low", "medium", "high"];
const MAX_REF_BYTES = 4 * 1024 * 1024; // 4MB decoded ⇒ well under the 6MB base64 backend cap.

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("File read failed."));
    reader.onload = () => {
      const s = String(reader.result || "");
      const i = s.indexOf(",");
      resolve({ base64: i >= 0 ? s.slice(i + 1) : s, mime: file.type || "image/png" });
    };
    reader.readAsDataURL(file);
  });

export const AiImagePicker = ({ onSelect, onClose, contextLabel, allowReference = true, defaultPrompt = "" }) => {
  const [mode, setMode] = useState("text"); // "text" | "reference"
  const [model, setModel] = useState("nano-banana");
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [aspect, setAspect] = useState("16:9");
  const [quality, setQuality] = useState("medium");
  const [count, setCount] = useState(1);
  const [refPreview, setRefPreview] = useState(null); // {base64, mime, dataUrl, name, size}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/ai-studio/history?limit=24`, { credentials: "include" });
      if (r.ok) setHistory(await r.json());
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Reference mode requires nano-banana
  useEffect(() => {
    if (mode === "reference" && model !== "nano-banana") setModel("nano-banana");
  }, [mode, model]);

  const pickFile = () => fileInputRef.current?.click();
  const handleFile = async (e) => {
    setError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(f.type)) {
      setError("Reference must be PNG, JPEG or WEBP.");
      return;
    }
    if (f.size > MAX_REF_BYTES) {
      setError("Reference image is larger than 4MB. Please compress and retry.");
      return;
    }
    try {
      const { base64, mime } = await readAsBase64(f);
      setRefPreview({ base64, mime, dataUrl: `data:${mime};base64,${base64}`, name: f.name, size: f.size });
    } catch (err) {
      setError(String(err.message || err));
    }
  };
  const clearRef = () => { setRefPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  const canGenerate = useMemo(() => {
    if (busy) return false;
    if (prompt.trim().length < 4) return false;
    if (mode === "reference" && !refPreview) return false;
    return true;
  }, [busy, prompt, mode, refPreview]);

  const generate = async () => {
    setError("");
    setBusy(true);
    setImages([]);
    try {
      const body = {
        model,
        prompt: prompt.trim(),
        aspect_ratio: aspect,
        quality,
        count,
      };
      if (mode === "reference" && refPreview) {
        body.reference_image_base64 = refPreview.base64;
        body.reference_mime_type = refPreview.mime;
      }
      const r = await fetch(`${API}/api/ai-studio/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setImages(data);
      await loadHistory();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const approve = (img) => {
    onSelect && onSelect({ ...img, prompt: prompt.trim(), model });
  };

  const pickFromHistory = (h) => {
    onSelect && onSelect({ id: h.id, url: h.url, prompt: h.prompt, model: h.model, mime_type: h.mime_type || "image/png", size: h.size || 0 });
  };

  return (
    <div
      data-testid="ai-image-picker"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(26,23,20,.55)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem",
      }}
    >
      <div
        style={{
          background: "var(--paper)", width: "min(1080px, 96vw)", maxHeight: "94vh",
          overflow: "auto", border: "1px solid var(--line)", boxShadow: "0 30px 80px rgba(0,0,0,.35)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.4rem", borderBottom: "1px solid var(--line)", background: "var(--paper-raised)" }}>
          <div>
            <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>
              AI Image · {contextLabel || "Studio"}
            </p>
            <h3 style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: ".2rem 0 0", letterSpacing: "-.005em" }}>Generate or pick an image</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="ai-picker-close" className="btn btn-ghost" style={{ padding: ".4rem .9rem", fontSize: ".85rem" }}>Close</button>
        </div>

        {/* Mode tabs */}
        {allowReference && (
          <div style={{ display: "flex", borderBottom: "1px solid var(--line)", background: "var(--paper-raised)" }}>
            {[
              { id: "text", label: "Text-to-image" },
              { id: "reference", label: "Image-to-image · reference" },
              { id: "history", label: "Pick from history" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`ai-picker-mode-${t.id}`}
                onClick={() => { if (t.id === "history") setShowHistory(true); else { setShowHistory(false); setMode(t.id); } }}
                style={{
                  padding: ".8rem 1.2rem", background: "transparent", border: 0, cursor: "pointer",
                  fontFamily: "var(--mono)", fontSize: ".7rem", letterSpacing: ".1em", textTransform: "uppercase",
                  color: (t.id === "history" ? showHistory : (mode === t.id && !showHistory)) ? "var(--ink)" : "var(--ink-soft)",
                  borderBottom: (t.id === "history" ? showHistory : (mode === t.id && !showHistory)) ? "2px solid var(--brass-deep)" : "2px solid transparent",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {showHistory ? (
          <section style={{ padding: "1.2rem 1.4rem" }}>
            {history.length === 0 ? (
              <p style={{ color: "var(--ink-soft)", fontStyle: "italic" }}>No generations yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: ".8rem" }} data-testid="ai-picker-history-grid">
                {history.map((h) => (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => pickFromHistory(h)}
                    data-testid={`ai-picker-history-${h.id}`}
                    style={{ padding: 0, border: "1px solid var(--line)", background: "var(--paper)", cursor: "pointer", textAlign: "left" }}
                  >
                    <img src={`${API}${h.url}`} alt={h.prompt?.slice(0, 60) || "Generated"} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block", background: "#f5f5f4" }} />
                    <div style={{ padding: ".4rem .5rem", fontSize: ".72rem", color: "var(--ink-soft)" }}>
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.prompt}</div>
                      <div className="mono" style={{ fontSize: ".62rem", letterSpacing: ".06em", marginTop: ".15rem", color: "var(--brass-deep)" }}>
                        {h.model}{h.has_reference ? " · ref" : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 0 }}>
            {/* Left · Controls */}
            <section style={{ padding: "1.2rem 1.4rem", borderRight: "1px solid var(--line)" }}>
              <label style={{ display: "block", marginBottom: "1rem" }}>
                <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Model</p>
                {MODEL_OPTIONS.map((m) => {
                  const disabled = mode === "reference" && m.id !== "nano-banana";
                  return (
                    <label
                      key={m.id}
                      style={{
                        display: "flex", gap: ".5rem", padding: ".55rem .7rem", marginBottom: ".3rem",
                        border: `1px solid ${model === m.id && !disabled ? "var(--brass-deep)" : "var(--line)"}`,
                        opacity: disabled ? .45 : 1, cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="ai-picker-model"
                        value={m.id}
                        checked={model === m.id}
                        onChange={() => !disabled && setModel(m.id)}
                        disabled={disabled}
                        data-testid={`ai-picker-model-${m.id}`}
                      />
                      <div>
                        <p style={{ margin: 0, fontFamily: "var(--display)", fontSize: ".9rem" }}>{m.label}</p>
                        <p style={{ margin: 0, fontSize: ".72rem", color: "var(--ink-soft)", lineHeight: 1.4 }}>{m.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </label>

              {mode === "reference" && (
                <label style={{ display: "block", marginBottom: "1rem" }}>
                  <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Reference image (PNG/JPEG/WEBP · ≤4MB)</p>
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} data-testid="ai-picker-ref-file" style={{ display: "none" }} />
                  {refPreview ? (
                    <div style={{ border: "1px solid var(--line)", padding: ".4rem", background: "var(--paper-raised)" }}>
                      <img src={refPreview.dataUrl} alt="Reference" style={{ width: "100%", maxHeight: 180, objectFit: "contain", display: "block", background: "#f5f5f4" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: ".5rem", fontSize: ".74rem", color: "var(--ink-soft)" }}>
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "60%" }}>{refPreview.name}</span>
                        <div style={{ display: "flex", gap: ".4rem" }}>
                          <button type="button" onClick={pickFile} className="btn btn-ghost" style={{ padding: ".2rem .55rem", fontSize: ".75rem" }} data-testid="ai-picker-ref-replace">Replace</button>
                          <button type="button" onClick={clearRef} className="btn btn-ghost" style={{ padding: ".2rem .55rem", fontSize: ".75rem" }} data-testid="ai-picker-ref-clear">Remove</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={pickFile} data-testid="ai-picker-ref-upload" style={{ width: "100%", padding: "1.2rem", border: "1px dashed var(--line)", background: "var(--paper-raised)", cursor: "pointer", fontFamily: "inherit", color: "var(--ink-soft)" }}>
                      Click to upload a reference image
                    </button>
                  )}
                </label>
              )}

              <label style={{ display: "block", marginBottom: "1rem" }}>
                <p className="mono" style={{ fontSize: ".62rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .4rem" }}>Prompt / instruction</p>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={mode === "reference" ? "Restyle this brass lamp in a rustic Provencal kitchen with morning light" : "A brass pendant lamp with hand-blown glass shade above a sheesham dining table…"}
                  rows={5}
                  data-testid="ai-picker-prompt"
                  style={{ width: "100%", padding: ".55rem .7rem", fontFamily: "inherit", fontSize: ".88rem", lineHeight: 1.5, border: "1px solid var(--line)", background: "#fff", resize: "vertical" }}
                />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: "1rem" }}>
                <label>
                  <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>Aspect</p>
                  <select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="ai-picker-aspect" style={selectStyle}>
                    {ASPECTS.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </label>
                <label>
                  <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>Quality</p>
                  <select value={quality} onChange={(e) => setQuality(e.target.value)} data-testid="ai-picker-quality" style={selectStyle}>
                    {QUALITIES.map((q) => <option key={q}>{q}</option>)}
                  </select>
                </label>
              </div>

              <label style={{ display: "block", marginBottom: "1rem" }}>
                <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: "0 0 .3rem" }}>Variations (1-4)</p>
                <input type="number" min={1} max={4} value={count} onChange={(e) => setCount(Math.max(1, Math.min(4, Number(e.target.value) || 1)))} data-testid="ai-picker-count" style={selectStyle} />
              </label>

              <button
                type="button"
                onClick={generate}
                disabled={!canGenerate}
                data-testid="ai-picker-generate"
                className="btn btn-primary"
                style={{ width: "100%", padding: ".65rem", fontSize: ".92rem" }}
              >
                {busy ? "Generating…" : (images.length ? "Regenerate" : "Generate")}
              </button>
              {error && (
                <div data-testid="ai-picker-error" style={{ marginTop: ".8rem", padding: ".6rem .8rem", border: "1px solid #b91c1c", background: "#fef2f2", color: "#7f1d1d", fontSize: ".82rem" }}>{error}</div>
              )}
            </section>

            {/* Right · Results */}
            <section style={{ padding: "1.2rem 1.4rem" }}>
              <p className="mono" style={{ fontSize: ".6rem", letterSpacing: ".14em", color: "var(--brass-deep)", textTransform: "uppercase", margin: 0 }}>
                {busy ? "Generating…" : (images.length ? `${images.length} candidate${images.length > 1 ? "s" : ""} — approve one` : "Preview will appear here")}
              </p>
              {busy && (
                <div data-testid="ai-picker-loading" style={{ padding: "2rem", border: "1px dashed var(--line)", textAlign: "center", color: "var(--ink-soft)", marginTop: ".8rem" }}>
                  Contacting {MODEL_OPTIONS.find((m) => m.id === model)?.label}…
                </div>
              )}
              {!busy && images.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem", marginTop: ".8rem" }} data-testid="ai-picker-results">
                  {images.map((img) => (
                    <figure key={img.id} data-testid={`ai-picker-result-${img.id}`} style={{ margin: 0, border: "1px solid var(--line)", background: "#fff" }}>
                      <img src={`${API}${img.url}`} alt="Generated candidate" style={{ width: "100%", display: "block", background: "#f5f5f4" }} />
                      <figcaption style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".55rem .7rem", fontSize: ".78rem", color: "var(--ink-soft)" }}>
                        <span>{Math.round(img.size / 1024)} KB</span>
                        <div style={{ display: "flex", gap: ".4rem" }}>
                          <a href={`${API}${img.url}`} download={`${img.id}.png`} target="_blank" rel="noreferrer" className="btn btn-ghost" data-testid={`ai-picker-download-${img.id}`} style={{ padding: ".2rem .55rem", fontSize: ".75rem" }}>Download</a>
                          <button type="button" onClick={() => approve(img)} className="btn btn-primary" data-testid={`ai-picker-approve-${img.id}`} style={{ padding: ".25rem .7rem", fontSize: ".78rem" }}>Use this</button>
                        </div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const selectStyle = { width: "100%", padding: ".45rem .55rem", fontFamily: "inherit", fontSize: ".88rem", border: "1px solid var(--line)", background: "#fff" };

export default AiImagePicker;
